'use client'

import { useTranslations } from 'next-intl'

import { ChevronDown } from 'lucide-react'

import type { PlanDestination } from '@/lib/homepage/plan-interlinks'
import { useIsBreakpoint } from '@/hooks/use-breakpoint'

import { InteractionLink, trackInteraction } from './interaction-link'

/**
 * Presentational "Plan your next adventure" accordion (homepage spec FR-11):
 * one collapsible `<details>` per destination city, each splitting its routes
 * into travel-mode sections (Trains / Buses / …) of C2C links with "from"
 * prices. Native `<details>` keeps every link in the server-rendered HTML
 * (crawlable, works without JS). Copy comes from the `homepage` i18n namespace;
 * the grouped route data is passed in.
 *
 * Layout: each city is assigned to a fixed column (independent stacks), so
 * opening one accordion grows only its own column — never reshuffling the
 * others (CSS multi-column) nor leaving a gap beside it (coupled grid rows).
 * The column *count* is responsive (1 mobile / 2 tablet / 3 desktop), which
 * has to be resolved client-side: independent columns require chunking the
 * list to the active count, and CSS can't re-chunk across breakpoints. Until
 * JS resolves it (SSR / no-JS), the list renders as a single column with every
 * link present.
 */
const MODE_LABEL_KEYS: Record<string, string> = {
    train: 'planModeTrains',
    bus: 'planModeBuses',
    ferry: 'planModeFerries',
}

/** Split into `count` sequential, balanced slices (down-column reading order). */
const toColumns = (groups: PlanDestination[], count: number): PlanDestination[][] => {
    const perColumn = Math.ceil(groups.length / count)
    return Array.from({ length: count }, (_, i) =>
        groups.slice(i * perColumn, (i + 1) * perColumn),
    ).filter((column) => column.length > 0)
}

const DestinationItem = ({ group }: { group: PlanDestination }) => {
    const t = useTranslations('homepage.popularRoutes')
    const modeLabel = (mode: string) => (MODE_LABEL_KEYS[mode] ? t(MODE_LABEL_KEYS[mode]) : mode)

    const handleToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
        trackInteraction('Homepage Interacted', {
            interaction_type: 'destination_toggled',
            destination: group.city,
            expanded: event.currentTarget.open,
        })
    }

    // Visual-order rank of each route within this destination (mode sections
    // render sequentially, so positions run down the whole destination list).
    const positionOffset = (modeIndex: number) =>
        group.modes.slice(0, modeIndex).reduce((count, m) => count + m.routes.length, 0)

    return (
        <li>
            <details className="group border-b border-[#e5e8ed]" onToggle={handleToggle}>
                <summary className="flex cursor-pointer items-center justify-between py-3 font-bold text-[#2e3033]">
                    {group.city}
                    <ChevronDown
                        aria-hidden="true"
                        className="size-5 shrink-0 text-[#787d87] transition-transform group-open:rotate-180"
                    />
                </summary>
                <div className="pb-4">
                    {group.modes.map((modeGroup, modeIndex) => (
                        <div key={modeGroup.mode} className="mt-2">
                            <div className="text-xs font-bold tracking-wide text-[#5f6368] uppercase">
                                {modeLabel(modeGroup.mode)}
                            </div>
                            <ul>
                                {modeGroup.routes.map((route, routeIndex) => (
                                    <li
                                        key={route.url}
                                        className="flex items-center justify-between gap-3 py-1"
                                    >
                                        <InteractionLink
                                            href={route.url}
                                            properties={{
                                                interaction_type: 'route_clicked',
                                                ui_source: 'plan_route_list',
                                                route_url: route.url,
                                                position:
                                                    positionOffset(modeIndex) + routeIndex + 1,
                                                mode: modeGroup.mode,
                                            }}
                                            className="text-[#007380] hover:underline"
                                        >
                                            {route.label}
                                        </InteractionLink>
                                        {route.fromPrice > 0 && (
                                            <span className="shrink-0 text-sm text-[#44474a]">
                                                {t('planFromPrice', { price: route.fromPrice })}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </details>
        </li>
    )
}

export const PlanInterlinkAccordion = ({ groups }: { groups: PlanDestination[] }) => {
    const t = useTranslations('homepage.popularRoutes')

    // 1 column on mobile, 2 on tablet (sm–lg), 3 on desktop (lg+). Defaults to a
    // single column for SSR / no-JS, then resolves on the client.
    const belowSm = useIsBreakpoint('sm', { initializeWithValue: false, defaultValue: true })
    const belowLg = useIsBreakpoint('lg', { initializeWithValue: false, defaultValue: true })
    const columnCount = belowSm ? 1 : belowLg ? 2 : 3

    return (
        <section className="border-t border-[#e5e8ed] bg-[#f7f8fa] px-4 py-12 md:px-10 md:py-16">
            <div className="mx-auto max-w-[1100px] text-center">
                <h2 className="text-2xl font-extrabold tracking-tight">{t('planHeading')}</h2>
                <p className="mt-2 text-[#5f6368]">{t('planSubtitle')}</p>
            </div>
            <div
                data-testid="plan-columns"
                className="mx-auto mt-8 flex max-w-[1100px] items-start gap-x-10"
            >
                {toColumns(groups, columnCount).map((column, i) => (
                    <ul key={i} className="flex-1">
                        {column.map((group) => (
                            <DestinationItem key={group.city} group={group} />
                        ))}
                    </ul>
                ))}
            </div>
        </section>
    )
}
